import React, { useMemo, useState } from 'react';
import { Product, Transaction, TransactionItem } from '../types';
import { usePos } from '../context/PosContext';
import { formatDateTime } from '../services/datetime';
import { printReceiptAuto } from '../services/receipt';
import PaymentModal from '../components/PaymentModal';
import './Sections.css';

const EditBill: React.FC = () => {
  const { sales, products, voidSale, updateSale, config, formatMoney, isOn } = usePos();
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Transaction | null>(null);
  // Working draft while editing a bill (null = not editing).
  const [draft, setDraft] = useState<Transaction | null>(null);
  // Unsettled (pending) bill being settled via the payment dialog.
  const [settleTx, setSettleTx] = useState<Transaction | null>(null);
  const [itemSearch, setItemSearch] = useState('');
  // The original bill's effective tax model, captured when editing opens so it
  // survives item/discount edits. `taxRatio` = tax ÷ subtotal preserves the
  // bill's blended rate (and inclusive-extracted ratio); `inclusive` records
  // whether tax was already inside the total (so we don't add it on top again).
  const [taxModel, setTaxModel] = useState<{ ratio: number; inclusive: boolean }>({
    ratio: 0,
    inclusive: false,
  });

  // Open the edit modal, snapshotting the bill's tax model first.
  const openEdit = (tx: Transaction) => {
    const ratio = tx.subtotal > 0 ? tx.tax / tx.subtotal : 0;
    const delivery = tx.parcelCharges ?? 0;
    const inclBase = tx.subtotal - tx.discount + delivery; // tax already inside
    const exclBase = inclBase + tx.tax; // tax added on top
    const inclusive = Math.abs(tx.total - inclBase) <= Math.abs(tx.total - exclBase);
    setTaxModel({ ratio, inclusive });
    setDraft({ ...tx, items: tx.items.map((i) => ({ ...i })) });
    setSelected(null);
  };

  const list = useMemo(() => {
    const t = search.trim().toLowerCase();
    return [...sales]
      .reverse()
      .filter(
        (s) =>
          !t ||
          s.billNo.toLowerCase().includes(t) ||
          s.customerName?.toLowerCase().includes(t) ||
          s.table?.toLowerCase().includes(t)
      );
  }, [sales, search]);

  // A bill is editable while it's a normal sale (not voided, not a return).
  // An unsettled (pending) bill additionally needs "14 — Bill Settlement /
  // B - Editing In Settlement" switched on.
  const editable = (tx: Transaction) =>
    tx.status !== 'cancelled' &&
    !tx.billNo.startsWith('R') &&
    (tx.status !== 'pending' || isOn('editing_in_settlement'));

  // Reprint using the same Print Settings-driven receipt as the POS screen.
  const reprint = (tx: Transaction) => {
    printReceiptAuto(tx, config).catch((e) => console.error('Reprint failed:', e));
  };

  // Bill Settlement: capture payment for a bill that was saved unsettled
  // (status 'pending'). The bill keeps its number; stock was already consumed
  // when it was saved, so settling just replaces the stored sale.
  const settle = (completed: Transaction): Transaction => {
    updateSale({ ...completed, status: 'completed' });
    setSettleTx(null);
    return { ...completed, status: 'completed' };
  };

  const handleVoid = (tx: Transaction) => {
    if (window.confirm(`Void bill ${tx.billNo}? This cannot be undone.`)) {
      voidSale(tx.id);
      if (selected?.id === tx.id) setSelected({ ...tx, status: 'cancelled' });
    }
  };

  // ---- Editing helpers (operate on the draft) ----
  const itemResults = useMemo(() => {
    const t = itemSearch.trim().toLowerCase();
    if (!t) return [];
    return products
      .filter((p) => p.name.toLowerCase().includes(t) || p.barcode?.includes(itemSearch))
      .slice(0, 8);
  }, [itemSearch, products]);

  const setQty = (productId: string, unitPrice: number, qty: number) =>
    setDraft((d) =>
      d
        ? {
            ...d,
            items: d.items
              .map((i) =>
                i.productId === productId && i.unitPrice === unitPrice
                  ? { ...i, quantity: qty, total: unitPrice * qty }
                  : i
              )
              .filter((i) => i.quantity > 0),
          }
        : d
    );

  const addItem = (p: Product) => {
    setDraft((d) => {
      if (!d) return d;
      const match = d.items.find((i) => i.productId === p.id && i.unitPrice === p.price);
      if (match) {
        return {
          ...d,
          items: d.items.map((i) =>
            i === match ? { ...i, quantity: i.quantity + 1, total: i.unitPrice * (i.quantity + 1) } : i
          ),
        };
      }
      const line: TransactionItem = {
        productId: p.id,
        name: p.name,
        quantity: 1,
        unitPrice: p.price,
        taxRatePercent: p.taxRatePercent ?? config.taxRatePercent,
        total: p.price,
      };
      return { ...d, items: [...d.items, line] };
    });
    setItemSearch('');
  };

  const setDiscount = (value: string) =>
    setDraft((d) => (d ? { ...d, discount: Math.max(0, parseFloat(value) || 0) } : d));

  const setPayment = (method: Transaction['paymentMethod']) =>
    setDraft((d) => (d ? { ...d, paymentMethod: method } : d));

  // Live totals for the draft, preserving the original bill's tax model. Tax is
  // the original effective ratio applied to the new subtotal; the total adds tax
  // on top only for exclusive-tax bills (inclusive bills already contain it).
  const draftTotals = useMemo(() => {
    if (!draft) return { subtotal: 0, tax: 0, total: 0, roundOff: 0 };
    const subtotal = draft.items.reduce((s, i) => s + i.total, 0);
    const discount = draft.discount ?? 0;
    const delivery = draft.parcelCharges ?? 0;
    const tax = subtotal * taxModel.ratio;
    const base = Math.max(0, subtotal - discount) + delivery;
    const raw = taxModel.inclusive ? base : base + tax;
    const total = Math.round(raw);
    return { subtotal, tax, total, roundOff: total - raw };
  }, [draft, taxModel]);

  const saveDraft = () => {
    if (!draft) return;
    if (draft.items.length === 0) {
      window.alert('A bill must have at least one item. Void it instead to cancel.');
      return;
    }
    const finalized: Transaction = {
      ...draft,
      subtotal: draftTotals.subtotal,
      tax: draftTotals.tax,
      roundOff: draftTotals.roundOff,
      total: draftTotals.total,
    };
    updateSale(finalized);
    setDraft(null);
    setSelected(null);
  };

  return (
    <div className="section-page">
      <div className="page-header">
        <h1>Edit Bill</h1>
        <input
          placeholder="Search bill no / customer / table"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ minWidth: 280 }}
        />
      </div>

      <div className="section-card">
        <h2>Bills ({list.length})</h2>
        {list.length === 0 ? (
          <p className="muted">No bills yet.</p>
        ) : (
          <table className="section-table">
            <thead>
              <tr>
                <th>Bill No</th>
                <th>Date</th>
                <th>Customer</th>
                <th className="right">Total</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {list.map((tx) => (
                <tr key={tx.id}>
                  <td>{tx.billNo}</td>
                  <td>{formatDateTime(tx.timestamp)}</td>
                  <td>{tx.customerName ?? '—'}</td>
                  <td className="right">{formatMoney(tx.total)}</td>
                  <td>
                    <span className={`badge ${tx.status === 'cancelled' ? 'cancelled' : 'completed'}`}>
                      {tx.status}
                    </span>
                  </td>
                  <td>
                    <button className="btn-grey" onClick={() => setSelected(tx)}>
                      View
                    </button>{' '}
                    {tx.status === 'pending' && (
                      <button className="btn-teal" onClick={() => setSettleTx(tx)}>
                        Settle
                      </button>
                    )}{' '}
                    {editable(tx) && (
                      <button className="btn-teal" onClick={() => openEdit(tx)}>
                        Edit
                      </button>
                    )}{' '}
                    <button className="btn-grey" onClick={() => reprint(tx)}>
                      Reprint
                    </button>{' '}
                    {tx.status !== 'cancelled' && (
                      <button className="btn-red" onClick={() => handleVoid(tx)}>
                        Void
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* View modal */}
      {selected && (
        <div className="modal-overlay" onClick={() => setSelected(null)}>
          <div className="section-card" style={{ minWidth: 420, maxWidth: 560 }} onClick={(e) => e.stopPropagation()}>
            <h2>
              {selected.billNo} — {formatDateTime(selected.timestamp)}
            </h2>
            <p className="muted" style={{ marginBottom: 10 }}>
              {selected.customerName ?? 'Walk-in'} {selected.table ? `· Table ${selected.table}` : ''} ·{' '}
              {selected.orderType ?? 'dinein'}
            </p>
            <table className="section-table">
              <thead>
                <tr>
                  <th>Item</th>
                  <th className="num-cell">Qty</th>
                  <th className="right">Price</th>
                  <th className="right">Total</th>
                </tr>
              </thead>
              <tbody>
                {selected.items.map((i) => (
                  <tr key={`${i.productId}-${i.unitPrice}`}>
                    <td>{i.name}</td>
                    <td>{i.quantity}</td>
                    <td className="right">{formatMoney(i.unitPrice)}</td>
                    <td className="right">{formatMoney(i.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="totals-line">Total: {formatMoney(selected.total)}</div>
            <div style={{ marginTop: 12, textAlign: 'right' }}>
              {editable(selected) && (
                <button className="btn-teal" onClick={() => openEdit(selected)}>
                  Edit
                </button>
              )}{' '}
              <button className="btn-grey" onClick={() => reprint(selected)}>
                Reprint
              </button>{' '}
              <button className="btn-grey" onClick={() => setSelected(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit modal */}
      {draft && (
        <div className="modal-overlay" onClick={() => setDraft(null)}>
          <div className="section-card" style={{ minWidth: 460, maxWidth: 620 }} onClick={(e) => e.stopPropagation()}>
            <h2>Edit Bill {draft.billNo}</h2>

            <div className="field-row">
              <div className="field">
                <label>Customer</label>
                <input
                  value={draft.customerName ?? ''}
                  placeholder="Walk-in"
                  onChange={(e) => setDraft((d) => (d ? { ...d, customerName: e.target.value } : d))}
                />
              </div>
              <div className="field">
                <label>Payment Mode</label>
                <select value={draft.paymentMethod} onChange={(e) => setPayment(e.target.value as Transaction['paymentMethod'])}>
                  <option value="cash">Cash</option>
                  <option value="card">Card</option>
                  <option value="digital">Digital</option>
                  <option value="credit">Credit</option>
                </select>
              </div>
            </div>

            {/* Add item */}
            <div className="field" style={{ position: 'relative', marginBottom: 12 }}>
              <label>Add item</label>
              <input
                value={itemSearch}
                placeholder="Search product by name / barcode…"
                onChange={(e) => setItemSearch(e.target.value)}
              />
              {itemResults.length > 0 && (
                <ul className="search-results" style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 5, background: '#fff' }}>
                  {itemResults.map((p) => (
                    <li key={p.id} onClick={() => addItem(p)}>
                      <span>{p.name}</span>
                      <span className="res-price">{formatMoney(p.price)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <table className="section-table">
              <thead>
                <tr>
                  <th>Item</th>
                  <th className="right">Price</th>
                  <th style={{ textAlign: 'center' }}>Qty</th>
                  <th className="right">Total</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {draft.items.map((i) => (
                  <tr key={`${i.productId}-${i.unitPrice}`}>
                    <td>{i.name}</td>
                    <td className="right">{formatMoney(i.unitPrice)}</td>
                    <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                      <button className="qty-btn" onClick={() => setQty(i.productId, i.unitPrice, i.quantity - 1)}>−</button>
                      <span style={{ margin: '0 8px' }}>{i.quantity}</span>
                      <button className="qty-btn" onClick={() => setQty(i.productId, i.unitPrice, i.quantity + 1)}>+</button>
                    </td>
                    <td className="right">{formatMoney(i.total)}</td>
                    <td>
                      <button className="row-del" title="Remove" onClick={() => setQty(i.productId, i.unitPrice, 0)}>×</button>
                    </td>
                  </tr>
                ))}
                {draft.items.length === 0 && (
                  <tr>
                    <td colSpan={5} className="muted">No items — add at least one or cancel.</td>
                  </tr>
                )}
              </tbody>
            </table>

            <div className="field-row" style={{ justifyContent: 'flex-end', marginTop: 12 }}>
              <div className="field" style={{ maxWidth: 160, flex: '0 0 auto' }}>
                <label>Discount</label>
                <input type="number" min={0} value={draft.discount ?? 0} onChange={(e) => setDiscount(e.target.value)} />
              </div>
            </div>

            <div style={{ textAlign: 'right', lineHeight: 1.7 }}>
              <div className="muted">Subtotal: {formatMoney(draftTotals.subtotal)}</div>
              <div className="muted">
                Tax{taxModel.inclusive ? ' (incl.)' : ''}: {formatMoney(draftTotals.tax)}
              </div>
              {(draft.parcelCharges ?? 0) > 0 && (
                <div className="muted">Delivery: {formatMoney(draft.parcelCharges ?? 0)}</div>
              )}
              <div className="muted">Discount: − {formatMoney(draft.discount ?? 0)}</div>
              <div className="totals-line" style={{ justifyContent: 'flex-end' }}>Total: {formatMoney(draftTotals.total)}</div>
            </div>

            <div style={{ marginTop: 14, textAlign: 'right' }}>
              <button className="btn-teal" onClick={saveDraft}>Save</button>{' '}
              <button className="btn-grey" onClick={() => setDraft(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Settle an unsettled (pending) bill — same payment dialog as the POS. */}
      {settleTx && (
        <PaymentModal
          transaction={settleTx}
          onComplete={settle}
          onCancel={() => setSettleTx(null)}
        />
      )}
    </div>
  );
};

export default EditBill;
